// src/utils/creditosAps.js
// Hectáreas por producto de cada mapeo de maquinaria: Aplicaciones Aéreas
// (Dron / Avioneta / Helicóptero × tasa normal / variada), Herbicidas
// (Dron / Autopropulsado × tasa normal / variada), Fertilización y Cosecha
// Mecánica. El saldo y el descuento viven en el backend; aquí solo se
// consulta, se avisa al usuario y se bloquea el botón.
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from './config';

export const TIPOS_CREDITOS = [
    { tipo: 'APLICACIONES_AEREAS', label: 'Aplicaciones Aéreas' },
    { tipo: 'HERBICIDAS', label: 'Herbicidas' },
    { tipo: 'FERTILIZACION', label: 'Fertilización' },
    { tipo: 'COSECHA_MECANICA', label: 'Cosecha Mecánica' },
];

// `equipo` y `modo` son los valores de los selectores de la barra.
export const PRODUCTOS_CREDITOS = [
    { producto: 'APS_DRON_NORMAL', tipo: 'APLICACIONES_AEREAS', equipo: 'DRONE', modo: 'CONVENCIONAL', nombre: 'Dron · Tasa normal' },
    { producto: 'APS_DRON_VRA', tipo: 'APLICACIONES_AEREAS', equipo: 'DRONE', modo: 'VAR', nombre: 'Dron · Tasa variada' },
    { producto: 'APS_AVIONETA_NORMAL', tipo: 'APLICACIONES_AEREAS', equipo: 'AVIONETA', modo: 'CONVENCIONAL', nombre: 'Avioneta · Tasa normal' },
    { producto: 'APS_AVIONETA_VRA', tipo: 'APLICACIONES_AEREAS', equipo: 'AVIONETA', modo: 'VAR', nombre: 'Avioneta · Tasa variada' },
    { producto: 'APS_HELICOPTERO_NORMAL', tipo: 'APLICACIONES_AEREAS', equipo: 'HELICOPTERO', modo: 'CONVENCIONAL', nombre: 'Helicóptero · Tasa normal' },
    { producto: 'APS_HELICOPTERO_VRA', tipo: 'APLICACIONES_AEREAS', equipo: 'HELICOPTERO', modo: 'VAR', nombre: 'Helicóptero · Tasa variada' },
    { producto: 'HERBICIDAS_DRON_NORMAL', tipo: 'HERBICIDAS', equipo: 'DRONE', modo: 'CONVENCIONAL', nombre: 'Dron · Tasa normal' },
    { producto: 'HERBICIDAS_DRON_VRA', tipo: 'HERBICIDAS', equipo: 'DRONE', modo: 'VAR', nombre: 'Dron · Tasa variada' },
    { producto: 'HERBICIDAS_AUTOPROPULSADO_NORMAL', tipo: 'HERBICIDAS', equipo: 'AUTOPROPULSADO', modo: 'CONVENCIONAL', nombre: 'Autopropulsado · Tasa normal' },
    { producto: 'HERBICIDAS_AUTOPROPULSADO_VRA', tipo: 'HERBICIDAS', equipo: 'AUTOPROPULSADO', modo: 'VAR', nombre: 'Autopropulsado · Tasa variada' },
    { producto: 'FERTILIZACION', tipo: 'FERTILIZACION', equipo: null, modo: null, nombre: 'Hectáreas aplicadas' },
    { producto: 'COSECHA_MECANICA', tipo: 'COSECHA_MECANICA', equipo: null, modo: null, nombre: 'Hectáreas recorridas' },
].map((p) => {
    const tipo = TIPOS_CREDITOS.find((t) => t.tipo === p.tipo).label;
    return { ...p, label: p.equipo ? `${tipo} · ${p.nombre}` : tipo };
});

// Nombre anterior (cuando solo existía Aplicaciones Aéreas).
export const PRODUCTOS_APS = PRODUCTOS_CREDITOS.filter((p) => p.tipo === 'APLICACIONES_AEREAS');

export const TIPOS_CON_CREDITOS = new Set(TIPOS_CREDITOS.map((t) => t.tipo));

export const CREDITOS_APS_EVENTO = 'creditosAps:actualizado';
// ID del análisis Normal que espera confirmar su consumo al calcular áreas.
export const CREDITOS_APS_PENDIENTE_KEY = 'creditosAps_pendiente';

export const claveProducto = (tipo, equipo, modo) => {
    if (tipo === 'FERTILIZACION' || tipo === 'COSECHA_MECANICA') return tipo;
    const e = String(equipo || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
    const m = String(modo || '').toUpperCase();
    const modoClave = m === 'VAR' || m === 'VRA' ? 'VRA' : 'NORMAL';
    if (tipo === 'HERBICIDAS') return `HERBICIDAS_${e.startsWith('AUTOPROP') ? 'AUTOPROPULSADO' : 'DRON'}_${modoClave}`;
    let eq = 'DRON';
    if (e.startsWith('HELI')) eq = 'HELICOPTERO';
    else if (e === 'AVIONETA' || e === 'AIRPLANE') eq = 'AVIONETA';
    return `APS_${eq}_${modoClave}`;
};

export const claveProductoAps = (equipo, modo) => claveProducto('APLICACIONES_AEREAS', equipo, modo);

// ¿La empresa controla las hectáreas de este mapeo? (respuesta de /creditos/aps)
export const controlaTipo = (creditos, tipo) => {
    if (!creditos) return false;
    if (Array.isArray(creditos.tipos)) return Boolean(creditos.tipos.find((t) => t.tipo === tipo)?.controlActivo);
    // Backend anterior: solo conocía Aplicaciones Aéreas.
    return tipo === 'APLICACIONES_AEREAS' && Boolean(creditos.controlActivo);
};

export const formatHa = (n) => `${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ha`;

const mensajeError = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

export const obtenerCreditosAps = async (idUsuario) => {
    if (!idUsuario) return null;
    const { data } = await axios.get(`${API_BASE_URL}dashboard/creditos/aps/${idUsuario}`);
    return data;
};

export const iniciarConsumoAps = async ({ idUsuario, idAnalisis, tipo = 'APLICACIONES_AEREAS', equipo, modo }) => {
    try {
        const { data } = await axios.post(`${API_BASE_URL}dashboard/creditos/aps/iniciar`, {
            idUsuario, idAnalisis: String(idAnalisis), tipo, equipo, modo,
        });
        if (data?.controlActivo) localStorage.setItem(CREDITOS_APS_PENDIENTE_KEY, String(idAnalisis));
        return data;
    } catch (error) {
        // Backend sin la ruta de créditos (aún sin desplegar): se trabaja como antes.
        if (error?.response?.status === 404) return { controlActivo: false, permitido: true };
        throw new Error(mensajeError(error, 'No fue posible validar el saldo de hectáreas.'));
    }
};

export const cancelarConsumoAps = async (idAnalisis, motivo) => {
    if (localStorage.getItem(CREDITOS_APS_PENDIENTE_KEY) === String(idAnalisis)) {
        localStorage.removeItem(CREDITOS_APS_PENDIENTE_KEY);
    }
    try {
        await axios.post(`${API_BASE_URL}dashboard/creditos/aps/cancelar`, { idAnalisis: String(idAnalisis), motivo });
    } catch (error) {
        console.warn('[CREDITOS_APS] No se pudo cerrar el pendiente:', error?.message);
    }
};

// Avisos al usuario tras un descuento (los mismos que manda el backend por correo).
export const notificarConsumoAps = (resultado) => {
    if (!resultado?.confirmado) return;
    toast.info(
        `Se descontaron ${formatHa(resultado.hectareasDescontadas)} de ${resultado.label}. Disponibles: ${formatHa(resultado.saldo?.hectareasDisponibles)}.`,
        { position: toast.POSITION.TOP_RIGHT, autoClose: 7000 }
    );
    (resultado.avisos || []).forEach((aviso) => {
        const fn = aviso.tipo === 'agotado' ? toast.error : toast.warning;
        fn(aviso.mensaje, { position: toast.POSITION.TOP_RIGHT, autoClose: 10000 });
    });
    window.dispatchEvent(new CustomEvent(CREDITOS_APS_EVENTO, { detail: resultado }));
};

// Modo Normal: se llama cuando calculate_areas respondió bien. Solo actúa si
// este navegador lanzó ese análisis (no cobra al reabrir uno del historial).
export const confirmarConsumoApsSiPendiente = async (idAnalisis) => {
    if (!idAnalisis || localStorage.getItem(CREDITOS_APS_PENDIENTE_KEY) !== String(idAnalisis)) return null;
    try {
        const { data } = await axios.post(`${API_BASE_URL}dashboard/creditos/aps/confirmar`, { idAnalisis: String(idAnalisis) });
        if (data?.confirmado || data?.motivo === 'sin_pendiente' || data?.motivo === 'ya_confirmado') {
            localStorage.removeItem(CREDITOS_APS_PENDIENTE_KEY);
        }
        notificarConsumoAps(data);
        return data;
    } catch (error) {
        console.warn('[CREDITOS_APS] No se pudo confirmar el consumo:', error?.message);
        return null;
    }
};
